<template>
    <component :is="componentType" class="vz-button" :to="to" :external="external" v-bind="$attrs">
        <slot v-if="$slots['icon-before']" name="icon-before"></slot>
        <span>
            <slot>CTA text</slot>
        </span>
        <slot v-if="$slots['icon-after']" name="icon-after"></slot>
    </component>
</template>

<script setup lang="ts">
    type Props = {
        to?: string;
        href?: string;
        external?: boolean;
    };

    const { to = undefined, href = undefined } = defineProps<Props>();

    const isNuxtLink = computed(() => !!to)
    const isExternalLink = computed(() => !!href && !to)
    const componentType = computed(() => {
        if (isNuxtLink.value) {
            return resolveComponent('NuxtLink');
        }

        if (isExternalLink.value) {
            return 'a';
        }

        return 'button';
    });
</script>

<style scoped lang="scss">
    .vz-button {
        background: linear-gradient(135deg,
                hsl(var(--primary-300)) 0%,
                hsl(var(--primary-500)) 50%,
                hsl(var(--primary-700)) 100%);
        background-position: 100% 0;
        background-size: 200%;
        border: none;
        border-radius: 0.25rem;
        box-sizing: border-box;
        color: white;
        cursor: pointer;
        display: inline-block;
        font-size: 1rem;
        font-weight: 600;
        opacity: 1;
        outline: none;
        padding: 0.75rem 1.5rem;
        text-decoration: none;
        transition-duration: 300ms;
        transition-property: opacity, background, color;
        transition-timing-function: ease-in-out;
        user-select: none;
        white-space: nowrap;
        width: max-content;
        will-change: background-color;

        &.hidden {
            opacity: 0;
            pointer-events: none;
        }

        .contents {
            align-items: center;
            display: inline-flex;
        }

        &:hover,
        &:focus-visible {
            background-position: 0 50%;
        }

        &:focus-visible {
            outline: 2px solid hsl(var(--white) / 70%);
            outline-offset: 2px;

            @media (prefers-contrast: more) {
                outline-color: hsl(var(--white));
                outline-offset: 3px;
            }
        }

        &:active {
            background-position: 100%;
        }

        &.center {
            place-self: center;
        }

        &[small] {
            font-size: 0.875rem;
            padding: 0.5rem 1rem;
        }

        &[text],
        &.text {
            background: none;
            border-block-end: 1px solid transparent;
            border-radius: 0;
            color: hsl(var(--primary-500));
            padding: 0;
            transition-property: opacity, color, border-color;

            &:hover,
            &:focus-visible {
                background: none;
                border-color: hsl(var(--primary-700));
                color: hsl(var(--primary-700));

                :deep(.vz-icon) {
                    color: hsl(var(--primary-700));
                    transition-property: color;
                }
            }

            &:active {
                background: none;
                color: hsl(var(--primary-900));
            }
        }

        &[light],
        &.light {
            background: linear-gradient(135deg,
                    hsl(var(--grey-200)) 0%,
                    hsl(var(--grey-300)) 50%,
                    hsl(var(--grey-400)) 100%);
            background-position: 100% 0;
            background-size: 200%;
            color: hsl(var(--grey-900));

            &:hover,
            &:focus-visible {
                background-position: 0 50%;
            }

            &:active {
                background-position: 100%;
            }
        }

        &[bordered] {
            background: none;
            background-color: transparent;
            border: 1px solid hsl(var(--grey-400));
            color: hsl(var(--primary-500));
            padding: 0.5rem 1.5rem;
            -webkit-tap-highlight-color: transparent;

            &:hover,
            &:focus-visible {
                background-color: hsl(var(--grey-400));
                color: hsl(var(--primary-700));
            }

            &:active {
                background-color: hsl(var(--primary-500));
                color: white;
            }
        }

        :deep(.vz-icon) {
            font-size: 1.5rem;
            margin-block-end: 0.125rem;
            vertical-align: middle;
        }

        &[disabled="true"] {
            cursor: pointer;
            opacity: 0.5;
            pointer-events: none;
        }
    }
</style>
